import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FaSearch } from 'react-icons/fa'
import styles from '../shared/mhlShared.module.scss'
import localStyles from './MilitaryHearingLossWebsiteVisitors.module.scss'
import { Pill } from '../shared/SourceTabs'
import type { JourneyStatus, WebformSignup, WebformSignupsResponse } from '../shared/mhlTypes'
import { formatDateTime, referralLabel, referralTone } from '../shared/mhlFormatting'
import { MHL_PAGE_SIZE, TablePagination, usePagedRows } from '../shared/TablePagination'

interface Props {
    data: WebformSignupsResponse | null
    loading: boolean
}

export const JOURNEY_LABELS: Record<JourneyStatus, string> = {
    not_signed: 'Not signed',
    signed_no_docs: 'Signed · ID docs missing',
    complete: 'Complete',
}

export const journeyTone = (status: JourneyStatus) =>
    status === 'complete' ? 'good' : status === 'signed_no_docs' ? 'warn' : 'bad'

const geoLabel = (s: WebformSignup) =>
    s.geo ? [s.geo.city, s.geo.region, s.geo.country].filter(Boolean).join(', ') || '—' : '—'

/** Clients who completed the public claim form, with their website journey alongside. */
export const WebformSignupsTable = ({ data, loading }: Props) => {
    const navigate = useNavigate()
    const [search, setSearch] = useState('')
    const [status, setStatus] = useState<'all' | JourneyStatus>('all')

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase()
        return (data?.signups || []).filter((s) => {
            if (status !== 'all' && s.journey_status !== status) return false
            if (!term) return true
            return (
                `${s.firstname} ${s.lastname}`.toLowerCase().includes(term) ||
                (s.email || '').toLowerCase().includes(term) ||
                (s.telephone || '').toLowerCase().includes(term) ||
                (s.postcode || '').toLowerCase().includes(term) ||
                (s.submission_ip || '').toLowerCase().includes(term) ||
                s.case_ref.toLowerCase().includes(term)
            )
        })
    }, [data, search, status])

    const { rows, page, totalPages, setPage, pageSize } = usePagedRows(filtered, MHL_PAGE_SIZE)

    return (
        <>
            <div className={styles.toolbar}>
                <div className={styles.searchWrap}>
                    <FaSearch className={styles.searchIcon} />
                    <input
                        className={styles.searchInput}
                        placeholder="Search name, email, phone, postcode, IP or case ref"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        aria-label="Search website signups"
                    />
                </div>
                <select className={styles.select} value={status} onChange={(e) => setStatus(e.target.value as 'all' | JourneyStatus)} aria-label="Journey status">
                    <option value="all">All journeys</option>
                    <option value="not_signed">Not signed</option>
                    <option value="signed_no_docs">Signed · ID docs missing</option>
                    <option value="complete">Complete</option>
                </select>
                <span className={styles.resultCount}>
                    {filtered.length} of {data?.signups.length ?? 0} signups
                </span>
            </div>

            <div className={styles.tableWrap}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Client</th>
                            <th>Signed up</th>
                            <th>Source</th>
                            <th>IP / location</th>
                            <th className="num">Visits before</th>
                            <th>Journey</th>
                            <th>Signed</th>
                            <th className="num">ID docs</th>
                            <th className="num">Chases</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && !data ? (
                            <tr>
                                <td colSpan={9} className={styles.emptyState}>
                                    Loading…
                                </td>
                            </tr>
                        ) : rows.length === 0 ? (
                            <tr>
                                <td colSpan={9} className={styles.emptyState}>
                                    No webform signups in this period.
                                </td>
                            </tr>
                        ) : (
                            rows.map((s) => (
                                <tr
                                    key={s.id}
                                    className={styles.rowClickable}
                                    onClick={() => navigate(`/military-hearing-loss/client/${s.client_id}`)}
                                >
                                    <td>
                                        <div className={styles.stackCell}>
                                            <span className={styles.stackPrimary}>
                                                {s.firstname} {s.lastname}
                                            </span>
                                            <span className={styles.stackSecondary}>
                                                {s.email || '—'}
                                                {s.telephone ? ` · ${s.telephone}` : ''}
                                            </span>
                                            <span className={`${styles.stackSecondary} ${localStyles.mono}`}>{s.case_ref}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={styles.stackSecondary}>{formatDateTime(s.submitted_at)}</span>
                                    </td>
                                    <td>
                                        <Pill tone={referralTone(s.lead_source)}>{referralLabel(s.lead_source)}</Pill>
                                    </td>
                                    <td>
                                        <div className={styles.stackCell}>
                                            <span className={localStyles.mono}>{s.submission_ip || 'IP not recorded'}</span>
                                            <span className={styles.stackSecondary}>{geoLabel(s)}</span>
                                        </div>
                                    </td>
                                    <td className="num">
                                        <div className={styles.stackCell}>
                                            <span className={styles.stackSecondary}>
                                                {s.submission_ip ? s.visits.before_signup : '—'}
                                            </span>
                                            {s.submission_ip && s.visits.first_visit_at && (
                                                <span className={styles.stackSecondary}>first {formatDateTime(s.visits.first_visit_at)}</span>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <Pill tone={journeyTone(s.journey_status)} dot>
                                            {JOURNEY_LABELS[s.journey_status]}
                                        </Pill>
                                    </td>
                                    <td>
                                        <span className={styles.stackSecondary}>
                                            {s.esign.signed ? formatDateTime(s.esign.signature_date) : '—'}
                                        </span>
                                    </td>
                                    <td className="num">
                                        <span className={styles.stackSecondary}>{s.identity_docs_uploaded}/2</span>
                                    </td>
                                    <td className="num">
                                        <div className={styles.stackCell}>
                                            <span className={styles.stackSecondary}>{s.chase.num_chases}</span>
                                            {s.chase.last_chase_date && (
                                                <span className={styles.stackSecondary}>last {formatDateTime(s.chase.last_chase_date)}</span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>

                <TablePagination page={page} totalPages={totalPages} total={filtered.length} pageSize={pageSize} onChange={setPage} />
            </div>
            <p className={localStyles.sectionNote} style={{ marginTop: 10 }}>
                Visits are matched to signups by IP address. Signups recorded before IP capture was added show "IP not
                recorded".
            </p>
        </>
    )
}
